import type { AppRole } from "@/contexts/AuthContext";

export interface TourStep {
  id: string;
  title: string;
  emoji: string;
  content: string;
  /** CSS selector for the spotlight target element. null = full-screen overlay */
  targetSelector: string | null;
  /** If set, user must click this selector before advancing */
  interactiveSelector?: string;
  interactiveHint?: string;
  /** Optional route to navigate to before showing this step */
  navigateTo?: string;
}

export interface TourTrack {
  role: AppRole;
  steps: TourStep[];
}

// ─── VIEWER TRACK ───
const viewerSteps: TourStep[] = [
  {
    id: "v-welcome",
    title: "Welcome to Nursly!",
    emoji: "🎉",
    content: "Hi {firstName}! You've been added to the Chadwell Heath Nursery & Pre-School platform. This quick tour will show you everything you need to know: it only takes a few minutes and by the end, you'll be ready to go.",
    targetSelector: null,
  },
  {
    id: "v-profile",
    title: "This is you!",
    emoji: "👤",
    content: "See your name and photo up here? This is your profile. You can update your photo, phone number, and emergency contact details anytime by clicking on your name.",
    targetSelector: "[data-tour='user-menu']",
  },
  {
    id: "v-sidebar",
    title: "Your menu",
    emoji: "📋",
    content: "This bar on the left is your main menu. It shows everything you have access to. Don't worry, you won't get lost! We'll walk through each section together.",
    targetSelector: "[data-sidebar='sidebar']",
  },
  {
    id: "v-alerts",
    title: "Your alerts",
    emoji: "🔔",
    content: "If anything needs your attention, like a reminder to complete the Room Register, it'll appear here as a notification. You'll also see a red dot on the bell icon at the top when something is waiting for you.",
    targetSelector: null,
    navigateTo: "/alerts",
  },
  {
    id: "v-shifts",
    title: "Checking your shifts",
    emoji: "📅",
    content: "Want to see when you're working? Your manager sets your shifts on the Rota, you can check this on the Rota page. You can see your own shifts but you can't edit them. If something looks wrong, speak to your manager.",
    targetSelector: null,
    navigateTo: "/rota",
  },
  {
    id: "v-done",
    title: "You're all set!",
    emoji: "🌟",
    content: "That's everything you need to know to use Nursly with confidence.\n\n✅ Check your Alerts regularly so nothing gets missed\n✅ Check the Rota to see your shifts\n\nIf you ever get stuck, tap the ❓ Help button at the bottom of the sidebar, it'll bring up the Help Manual or let you replay this tour anytime.",
    targetSelector: null,
  },
];

// ─── EDITOR TRACK ───
const editorSteps: TourStep[] = [
  {
    id: "e-welcome",
    title: "Welcome to Nursly!",
    emoji: "🎉",
    content: "Hi {firstName}! Welcome to the Chadwell Heath Nursery & Pre-School platform. As an Editor, you can view and suggest changes across the platform. Let's walk through everything together.",
    targetSelector: null,
  },
  {
    id: "e-profile",
    title: "This is you!",
    emoji: "👤",
    content: "Click your name or photo in the top-right corner anytime to update your personal details.",
    targetSelector: "[data-tour='user-menu']",
  },
  {
    id: "e-sidebar",
    title: "Your menu",
    emoji: "📋",
    content: "The sidebar shows all the pages you have access to. We'll walk through the most important ones now.",
    targetSelector: "[data-sidebar='sidebar']",
  },
  {
    id: "e-register",
    title: "The Register",
    emoji: "✅",
    content: "The Register is where attendance is recorded. You can mark children as present, absent, or record sign-in and sign-out times. If you make a mistake, there's a 5-minute undo button, after that, you'll need to submit a correction for your manager to approve.",
    targetSelector: null,
    navigateTo: "/register",
  },
  {
    id: "e-children",
    title: "Children",
    emoji: "👶",
    content: "The Children page shows all children enrolled at the nursery. You can view their profiles and suggest changes, but your changes go to your manager for approval before they take effect. Look for the 'Pending Approval' badge after submitting a change.",
    targetSelector: null,
    navigateTo: "/children",
  },
  {
    id: "e-staff",
    title: "Staff",
    emoji: "👥",
    content: "On the Staff page, you can see team members' names and job titles. Contact details and qualifications are visible to managers only.",
    targetSelector: null,
    navigateTo: "/staff",
  },
  {
    id: "e-rota",
    title: "Rota & Shifts",
    emoji: "📅",
    content: "The Rota page shows the weekly shift schedule. You can see shift types: Starter, Finisher, Mid-Day, Full Day, and more. You can view but not edit shifts.",
    targetSelector: null,
    navigateTo: "/rota",
  },
  {
    id: "e-approvals",
    title: "The Approval Process",
    emoji: "📝",
    content: "When you make a change on the platform, like updating a child's profile, it doesn't go live straight away. Your manager will receive a notification to review it. Once they approve it, the change takes effect. This keeps everything accurate and safe.",
    targetSelector: null,
  },
  {
    id: "e-alerts",
    title: "Your alerts",
    emoji: "🔔",
    content: "Check your Alerts page regularly. You'll be notified when your changes are approved, rejected, or if your manager needs more information.",
    targetSelector: null,
    navigateTo: "/alerts",
  },
  {
    id: "e-done",
    title: "You're all set!",
    emoji: "🌟",
    content: "You're ready to go!\n\n✅ Use the Register to record attendance each day\n✅ View and suggest changes on Children profiles\n✅ Check your Alerts regularly\n✅ Check the Rota for your shifts\n\nIf you ever get stuck, tap the ❓ Help button at the bottom of the sidebar.",
    targetSelector: null,
  },
];

// ─── ADMIN TRACK ───
const adminSteps: TourStep[] = [
  {
    id: "a-welcome",
    title: "Welcome to Nursly!",
    emoji: "🎉",
    content: "Hi {firstName}! As an Admin, you can approve changes, manage children and staff records, and access reports. Let's walk through your command centre.",
    targetSelector: null,
  },
  {
    id: "a-profile",
    title: "Your profile & security",
    emoji: "👤",
    content: "Click your name in the top-right to access your profile settings. You can update your details and manage your account from there.",
    targetSelector: "[data-tour='user-menu']",
  },
  {
    id: "a-sidebar",
    title: "Your menu",
    emoji: "📋",
    content: "The sidebar gives you access to every section of the platform. As an Admin, you have a lot of tools, let's go through the most important ones.",
    targetSelector: "[data-sidebar='sidebar']",
  },
  {
    id: "a-dashboard",
    title: "Your Dashboard",
    emoji: "📊",
    content: "The Dashboard is your command centre. It shows today's coverage, items needing your attention, children starting soon, certificate alerts, and register status. Check this every morning when you arrive.",
    targetSelector: null,
    navigateTo: "/dashboard",
  },
  {
    id: "a-approvals",
    title: "Approvals",
    emoji: "✅",
    content: "When an Editor makes a change, it lands here for you to review. You can Approve it, Reject it, or ask for more information before deciding. Try to check this daily so nothing waits too long.",
    targetSelector: null,
    navigateTo: "/approvals",
  },
  {
    id: "a-children",
    title: "Children",
    emoji: "👶",
    content: "You have full access to edit children's profiles: session times, funding status, parent contacts, and more. Some actions require the platform passcode for security.",
    targetSelector: null,
    navigateTo: "/children",
  },
  {
    id: "a-staff",
    title: "Staff",
    emoji: "👥",
    content: "Full staff profiles are available to you, including DBS status, First Aid certificates, and qualifications. Keep an eye on the Expiring Certs filter to stay on top of renewals.",
    targetSelector: null,
    navigateTo: "/staff",
  },
  {
    id: "a-rota",
    title: "Rota",
    emoji: "📅",
    content: "Build the weekly rota here. You can add shifts, use templates, copy previous weeks, and export for payroll. The compliance footer shows whether each zone meets its staffing ratio.",
    targetSelector: null,
    navigateTo: "/rota",
  },
  {
    id: "a-compliance",
    title: "Compliance",
    emoji: "🛡",
    content: "The Compliance page tracks DBS, First Aid, and qualification status across all staff. Items flagged as 'Action Required' need your immediate attention.",
    targetSelector: null,
    navigateTo: "/compliance",
  },
  {
    id: "a-audit",
    title: "Audit Log",
    emoji: "📜",
    content: "The Audit Log records every significant action on the platform. It's your traceable history of who did what and when, useful for accountability and compliance checks.",
    targetSelector: null,
    navigateTo: "/audit-log",
  },
  {
    id: "a-done",
    title: "You're all set!",
    emoji: "🌟",
    content: "You're ready to manage the platform with confidence.\n\n✅ Every morning: check the Dashboard\n✅ Every day: check Approvals\n✅ Every week: check the Rota and Compliance page\n✅ Every month: export the payroll report\n\nIf you ever get stuck, tap the ❓ Help button at the bottom of the sidebar.",
    targetSelector: null,
  },
];

// ─── SUPER ADMIN TRACK ───
const superAdminSteps: TourStep[] = [
  ...adminSteps.slice(0, -1), // Everything except the final "done" step
  {
    id: "sa-access",
    title: "Access Management",
    emoji: "🔐",
    content: "Here you can invite new team members, assign access levels, and change roles. Invitations expire after 48 hours, the new user sets up their own password.",
    targetSelector: null,
    navigateTo: "/access",
  },
  {
    id: "sa-passcode",
    title: "Platform Passcode",
    emoji: "🔢",
    content: "The platform passcode is a 4-digit code required for sensitive actions. As a Owner, you can change it and recover it if forgotten, either instantly via your password or via email.",
    targetSelector: null,
    navigateTo: "/settings",
  },
  {
    id: "sa-responsibility",
    title: "Your responsibility",
    emoji: "⚠️",
    content: "As a Owner, you have the highest level of access on this platform. This means you can see and change almost everything. With that comes responsibility, always use the passcode wisely and only share access with the right people.",
    targetSelector: null,
  },
  {
    id: "sa-done",
    title: "You're all set!",
    emoji: "🌟",
    content: "You have full control of Nursly.\n\n✅ Every morning: check the Dashboard\n✅ Every day: check Approvals\n✅ Every week: check Rota, Compliance, and Access\n✅ Every month: export the payroll report and review the Audit Log\n\nIf you ever get stuck, tap the ❓ Help button at the bottom of the sidebar.",
    targetSelector: null,
  },
];

// ─── FRONT DESK TRACK ───
const frontDeskSteps: TourStep[] = [
  {
    id: "fd-welcome",
    title: "Welcome to Nursly",
    emoji: "👋",
    content: "You have Front Desk access. That means reception and nursery administration: adding children, turning enrolment requests into children, greeting families, signing children in and out for any room, keeping child details up to date, archiving children who have left, and keeping staff DBS and First Aid renewals on track. Spreadsheet import and restoring archived children are Admin and Owner only.",
    targetSelector: null,
  },
  {
    id: "fd-register",
    title: "Register",
    emoji: "📋",
    content: "Sign children in and out here for any room, and tick club pickups and drop-offs. You can see who is in and who is still expected across the whole nursery.",
    targetSelector: null,
    navigateTo: "/register",
  },
  {
    id: "fd-children",
    title: "Children",
    emoji: "👶",
    content: "Every child is listed here with their room and attendance status. You can update contact details, session details, funding and SEND details.",
    targetSelector: null,
    navigateTo: "/children",
  },
  {
    id: "fd-rota",
    title: "Rota",
    emoji: "📅",
    content: "Build and change the rota for any room from here.",
    targetSelector: null,
    navigateTo: "/rota",
  },
  {
    id: "fd-certs",
    title: "DBS and First Aid renewals",
    emoji: "🗂",
    content: "On the Staff page you can record DBS and First Aid expiry dates, upload certificates, and chase renewals before they run out.",
    targetSelector: null,
    navigateTo: "/staff",
  },
  {
    id: "fd-done",
    title: "You're all set!",
    emoji: "🌟",
    content: "A few things stay with the care team: the medicine log, completing risk assessments, and safeguarding records such as LADO referrals. You will not see those options.\n\nIf you ever get stuck, tap the ❓ Help button at the bottom of the sidebar.",
    targetSelector: null,
  },
];

export type Plan = "starter" | "professional";

// Plan-specific add-ons appended before the final "-done" step
const starterExtraSteps: Record<AppRole, TourStep[]> = {
  viewer: [],
  editor: [],
  front_desk: [],
  admin: [
    {
      id: "plan-starter-cap",
      title: "You're on the Starter plan",
      emoji: "✨",
      content: "Starter is free and supports up to 25 active children. When you're ready to grow, upgrade to Professional (£49 a month, or £490 a year. 2 months free) to unlock unlimited children, the Pickup & Drop-off module, Out of School zones, the Migration Tool and more. Try Professional risk-free with our 14-day money-back guarantee on annual plans.",
      targetSelector: null,
    },
  ],
  super_admin: [
    {
      id: "plan-starter-cap-sa",
      title: "You're on the Starter plan",
      emoji: "✨",
      content: "Starter is free and supports up to 25 active children. Upgrade to Professional (£49 a month, or £490 a year. 2 months free) for unlimited children, Pickup & Drop-off, Out of School zones, the Migration Tool, advanced exports and more. Annual plans come with a 14-day money-back guarantee.",
      targetSelector: null,
      navigateTo: "/billing",
    },
    {
      id: "plan-starter-referrals",
      title: "Refer another nursery",
      emoji: "🎁",
      content: "Share your unique referral code from the Refer a Nursery page. When the nursery you refer subscribes to Professional, you'll get 2 months of Professional free, applied automatically.",
      targetSelector: null,
      navigateTo: "/refer",
    },
  ],
};

const professionalExtraSteps: Record<AppRole, TourStep[]> = {
  viewer: [],
  front_desk: [],
  editor: [
    {
      id: "plan-pro-pickup-editor",
      title: "Pickup & Drop-off",
      emoji: "🚗",
      content: "If your nursery runs the Pickup & Drop-off service, you'll see today's collections in the Pickup module. Mark each child as collected from school, arrived at the nursery, and handed back to parents.",
      targetSelector: null,
      navigateTo: "/pickups/today",
    },
  ],
  admin: [
    {
      id: "plan-pro-pickup",
      title: "Pickup & Drop-off",
      emoji: "🚗",
      content: "Manage school transfer bookings, assign staff to schools each day, and track every child through the three stages: collected from school, arrived at the nursery, and handed back to parents.",
      targetSelector: null,
      navigateTo: "/pickups/today",
    },
    {
      id: "plan-pro-out-of-school",
      title: "Out of School zone",
      emoji: "🎒",
      content: "If you run Breakfast Club, After School Club or Holiday Club, the Out of School zone tracks them separately at a 1:8 ratio. Funded hours don't apply here, children are charged the standard session rate.",
      targetSelector: null,
    },
  ],
  super_admin: [
    {
      id: "plan-pro-pickup-sa",
      title: "Pickup & Drop-off",
      emoji: "🚗",
      content: "Manage school transfer bookings, assign staff to schools each day, and track every child through the collection, arrival and hand-back stages.",
      targetSelector: null,
      navigateTo: "/pickups/today",
    },
    {
      id: "plan-pro-out-of-school-sa",
      title: "Out of School zone",
      emoji: "🎒",
      content: "Breakfast Club, After School Club and Holiday Club are managed in the Out of School zone: 1:8 ratio, no funded hours. Configure schools and sessions in Settings.",
      targetSelector: null,
    },
    {
      id: "plan-pro-migration",
      title: "Free Migration Service",
      emoji: "📦",
      content: "Switching from another system? Submit your existing children, staff and rota data via the Migration page and our team will import everything for you, free of charge.",
      targetSelector: null,
      navigateTo: "/migrate",
    },
    {
      id: "plan-pro-referrals",
      title: "Refer another nursery",
      emoji: "🎁",
      content: "Share your unique referral code from the Refer a Nursery page. For every nursery that signs up to Professional, you'll get 2 extra months of Professional free.",
      targetSelector: null,
      navigateTo: "/refer",
    },
  ],
};

const baseTracks: Record<AppRole, TourStep[]> = {
  viewer: viewerSteps,
  editor: editorSteps,
  front_desk: frontDeskSteps,
  admin: adminSteps,
  super_admin: superAdminSteps,
};

/** Build the tour for a given plan + role. Plan-specific steps are inserted before the final "-done" step. */
export function getTourSteps(plan: Plan, role: AppRole): TourStep[] {
  const base = baseTracks[role] || [];
  if (base.length === 0) return base;
  const extras = (plan === "professional" ? professionalExtraSteps : starterExtraSteps)[role] || [];
  if (extras.length === 0) return base;
  // Insert before the final "-done" step
  return [...base.slice(0, -1), ...extras, base[base.length - 1]];
}

/** Backwards-compat: default to professional if plan unknown. */
export const tourTracks: Record<AppRole, TourStep[]> = {
  viewer: getTourSteps("professional", "viewer"),
  editor: getTourSteps("professional", "editor"),
  front_desk: getTourSteps("professional", "front_desk"),
  admin: getTourSteps("professional", "admin"),
  super_admin: getTourSteps("professional", "super_admin"),
};

/** For the Help menu replay: group steps by label */
export function getTourSectionList(role: AppRole, plan: Plan = "professional"): { id: string; label: string; emoji: string }[] {
  const steps = getTourSteps(plan, role);
  return steps
    .filter(s => s.id.endsWith("-done") === false && s.id.endsWith("-welcome") === false)
    .map(s => ({ id: s.id, label: s.title, emoji: s.emoji }));
}

// ─── DELTA TOURS ───
// Short "what's new" tours shown once when a feature ships. Tracked per-user in
// profiles.delta_tours_completed as { [deltaKey]: ISO timestamp }.
export interface DeltaTour {
  key: string;
  title: string;
  appliesTo: (plan: Plan, role: AppRole) => boolean;
  steps: TourStep[];
}

export const deltaTours: DeltaTour[] = [
  {
    key: "2026-05-pickup-and-out-of-school",
    title: "What's new: Pickup & Out of School",
    appliesTo: (plan, role) => plan === "professional" && role !== "viewer",
    steps: [
      {
        id: "delta-pickup",
        title: "New: Pickup & Drop-off",
        emoji: "🚗",
        content: "You can now manage school collections from one place: bookings, daily staff assignments and a 3-stage tracker for every child.",
        targetSelector: null,
        navigateTo: "/pickups/today",
      },
      {
        id: "delta-out-of-school",
        title: "New: Out of School zone",
        emoji: "🎒",
        content: "Run Breakfast, After School and Holiday Clubs as a separate zone with a 1:8 ratio. Funded hours don't apply, standard session rates only.",
        targetSelector: null,
      },
      {
        id: "delta-pickup-done",
        title: "All caught up!",
        emoji: "✅",
        content: "That's everything new. You can replay this anytime from the ❓ Help menu.",
        targetSelector: null,
      },
    ],
  },
  {
    key: "2026-05-compliance-expansion",
    title: "What's new: Compliance expansion",
    appliesTo: (plan, role) => plan === "professional" && (role === "admin" || role === "super_admin"),
    steps: [
      {
        id: "delta-ce-welcome",
        title: "New: Full compliance suite",
        emoji: "🛡",
        content: "Compliance now covers risk assessments, fire safety, and medicine administration, everything Ofsted expects, in one place.",
        targetSelector: null,
        navigateTo: "/compliance",
      },
      {
        id: "delta-ce-risk",
        title: "Risk assessments",
        emoji: "📋",
        content: "A pre-populated library of every required assessment (fire, indoor, outdoor, kitchen, outings…). Each one tracks its review date with Current / Due Soon / Overdue badges.",
        targetSelector: null,
      },
      {
        id: "delta-ce-fire",
        title: "Fire safety",
        emoji: "🔥",
        content: "Log termly fire drills, track extinguisher and alarm service dates, and upload your evacuation procedure. Missing a termly drill flags as Critical.",
        targetSelector: null,
      },
      {
        id: "delta-ce-medicines",
        title: "Medicines",
        emoji: "💊",
        content: "Record parental consents and log every dose on each child's Medicines tab. Records are immutable, corrections are added as annotations to protect the audit trail.",
        targetSelector: null,
      },
      {
        id: "delta-ce-ofsted",
        title: "Ofsted Readiness recalibrated",
        emoji: "✅",
        content: "Your score now reflects all of the above. Critical breaches (ratio, PFA gap, unconsented meds, overdue drill) cap the score at 59% until fixed, with a 'Fix this now' link for each.",
        targetSelector: null,
        navigateTo: "/ofsted-readiness",
      },
      {
        id: "delta-ce-done",
        title: "All caught up!",
        emoji: "🎉",
        content: "Replay this anytime from the ❓ Help menu.",
        targetSelector: null,
      },
    ],
  },
  {
    key: "2026-06-kiosk-and-transitions",
    title: "What's new: Kiosk mode & smarter transitions",
    appliesTo: (plan, role) => plan === "professional" && (role === "admin" || role === "super_admin"),
    steps: [
      {
        id: "delta-kt-welcome",
        title: "Two new tools for the front door",
        emoji: "✨",
        content: "Kiosk mode for parent self sign-in, and automated age-based transitions so no child sits in the wrong room.",
        targetSelector: null,
      },
      {
        id: "delta-kt-kiosk",
        title: "Kiosk mode",
        emoji: "📱",
        content: "Settings → Kiosk gives you a unique URL to load on a tablet at the entrance. Parents tap their child's photo to sign in or out. A 4-digit exit passcode keeps the device locked to kiosk mode. Regenerate the URL anytime if the link is shared.",
        targetSelector: null,
        navigateTo: "/settings?tab=kiosk",
      },
      {
        id: "delta-kt-transitions-auto",
        title: "Age-based transition automation",
        emoji: "🪄",
        content: "Transitions are now proposed automatically 3 months before a child reaches the next zone's age threshold. The academic year cohort filter on the Transitions page lets you plan whole September moves together. Proposed dates can be edited or cancelled.",
        targetSelector: null,
        navigateTo: "/transitions",
      },
      {
        id: "delta-kt-medicines-consent",
        title: "Medicines consent recap",
        emoji: "💊",
        content: "Reminder: a dose cannot be logged without a valid parental consent on file. Add ongoing consents (Calpol, inhalers) from the child's Medicines tab. The Register shows an Unsigned Medicines banner if anything is missing.",
        targetSelector: null,
      },
      {
        id: "delta-kt-done",
        title: "All caught up!",
        emoji: "🎉",
        content: "Replay this anytime from the ❓ Help menu in the sidebar.",
        targetSelector: null,
      },
    ],
  },
];


